import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";
import { ModernizedActivityEntityType, type EditingTeamRole } from "@axio/graphql/generated/graphql";
import { GlobalCommentsSheet } from "./components/GlobalCommentsSheet";
import ExpandedReviewContextBar from "./components/ExpandedReviewContextBar";
import PublishSheet from "./components/PublishSheet";
import ReviewActionPanel from "./components/ReviewActionPanel";
import ReviewContentPanel from "./components/ReviewContentPanel";
import ReviewSidebar from "./components/ReviewSidebar";
import ReviewTopBar from "./components/ReviewTopBar";
import VersionHistorySheetContainer from "./components/VersionHistorySheetContainer";
import { useCommentThreads } from "./hooks/useCommentThreads";
import { TeamMembersProvider } from "./contexts/TeamMembersContext";
import type { TeamManagementUserOption } from "./components/TeamManagementPanel";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "./constants/itemTypeLabels";
import type {
  CourseModernizerReviewTeamManagement,
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFields,
} from "./types/reviewTypes";
import { colors } from "../../../../styles/design-system";

function getExpandedItemTypeLabel(item: CourseEvaluatorReviewSidebarItem): string {
  if (item.kind === "moduleDescription") return "Module Description";
  if (item.kind === "recommendation") return "Recommendation";

  const itemTypeMeta = item.meta?.find((metaEntry) => metaEntry.kind === "itemType");
  if (!itemTypeMeta || itemTypeMeta.kind !== "itemType") {
    return item.kind === "suggestedContent" ? "Suggested Content" : "Activity";
  }

  return (
    COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemTypeMeta.itemType] ??
    (item.kind === "suggestedContent" ? "Suggested Content" : "Activity")
  );
}

function isManualSidebarItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.meta?.some((metaEntry) => metaEntry.kind === "manual") === true;
}

function isNewSidebarItem(item: CourseEvaluatorReviewSidebarItem): boolean {
  return item.meta?.some((metaEntry) => metaEntry.kind === "tag" && metaEntry.tag === "new") === true;
}

interface ReviewPanelProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  isArchitect: boolean;
  currentUserId: string | null;
  teamManagement: CourseModernizerReviewTeamManagement | null;
  teamUserOptions: TeamManagementUserOption[];
  teamSearchQuery: string;
  onTeamSearchQueryChange: (value: string) => void;
  selectedTeamRole: EditingTeamRole;
  onSelectedTeamRoleChange: (role: EditingTeamRole) => void;
  isSavingTeamManagement: boolean;
  onEditingTeamChange: (nextMembers: Array<{ userId: string; role: EditingTeamRole }>) => void;
  selectedItemId: string | null;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  selectedDecision: CourseEvaluatorReviewDecision;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  reviewedProgress: number;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  onExitReview: () => void;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onStartReviewing: () => void;
  onAddModule?: () => void;
  onDeleteManualActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAddActivity?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onUndo?: () => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onPreviewIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  isGeneratingIle?: boolean;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  // Publish flow
  queuedPublishItems: QueuedPublishItem[];
  lastPublishedItems: PublishedPublishItem[];
  publishManualSteps: PublishManualStep[];
  publishDocumentUpdates: PublishDocumentUpdate[];
  undecidedCount: number;
  isPublishSheetOpen: boolean;
  publishStatus: PublishStatus;
  onOpenPublishSheet: () => void;
  onClosePublishSheet: () => void;
  onDonePublish: () => void;
  onPublish: () => void;
  onExportPdf: () => void;
  onExportImscc: () => void;
  isExportingPdf?: boolean;
  isExportingImscc?: boolean;
  isVersionHistoryOpen: boolean;
  onOpenVersionHistory: () => void;
  onCloseVersionHistory: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
  acceptHiddenReason?: string;
}

function ReviewPanel({
  review,
  activityDetails,
  isArchitect,
  currentUserId,
  teamManagement,
  teamUserOptions,
  teamSearchQuery,
  onTeamSearchQueryChange,
  selectedTeamRole,
  onSelectedTeamRoleChange,
  isSavingTeamManagement,
  onEditingTeamChange,
  selectedItemId,
  selectedReviewItem,
  selectedDecision,
  filterMode,
  collapsedSectionIds,
  reviewedProgress,
  getDecision,
  lastSavedAt,
  isSaving,
  onExitReview,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onStartReviewing,
  onAddModule,
  onDeleteManualActivity,
  onAddActivity,
  onAccept,
  onReject,
  onAcceptGroup,
  onRejectGroup,
  onUndo,
  onSaveEdit,
  onCreateIle,
  onPreviewIle,
  isGeneratingIle = false,
  onNavigateNext,
  onNavigatePrev,
  onJumpToEntity,
  queuedPublishItems,
  lastPublishedItems,
  publishManualSteps,
  publishDocumentUpdates,
  undecidedCount,
  isPublishSheetOpen,
  publishStatus,
  onOpenPublishSheet,
  onClosePublishSheet,
  onDonePublish,
  onPublish,
  onExportPdf,
  onExportImscc,
  isExportingPdf = false,
  isExportingImscc = false,
  isVersionHistoryOpen,
  onOpenVersionHistory,
  onCloseVersionHistory,
  canAcceptSelectedItem,
  acceptDisabledReason,
  acceptHiddenReason,
}: ReviewPanelProps) {
  const isSelectedItemPublished = selectedDecision === "synced";
  const [isOverviewOverlayVisible, setIsOverviewOverlayVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  useEffect(() => {
    if (!selectedReviewItem) {
      setIsExpanded(false);
    }
  }, [selectedReviewItem]);

  const orderedItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const selectedItemPosition = useMemo(() => {
    if (!selectedReviewItem) return -1;
    return orderedItems.findIndex((item) => item.id === selectedReviewItem.id);
  }, [orderedItems, selectedReviewItem]);

  const selectedActivityDetail = selectedReviewItem ? activityDetails[selectedReviewItem.id] : null;
  const deletableManualActivityIds = useMemo(
    () =>
      new Set(
        review.sidebarGroups
          .flatMap((group) => group.items)
          .filter(
            (item) =>
              (item.entityType === ModernizedActivityEntityType.Assignment ||
                item.entityType === ModernizedActivityEntityType.Project ||
                item.entityType === ModernizedActivityEntityType.OtherActivity ||
                item.entityType === ModernizedActivityEntityType.IleRecommendation) &&
              isNewSidebarItem(item) &&
              (isManualSidebarItem(item) || activityDetails[item.id]?.isManuallyAuthored === true)
          )
          .map((item) => item.id)
      ),
    [activityDetails, review.sidebarGroups]
  );
  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);
  const [commentPinOpenForItemId, setCommentPinOpenForItemId] = useState<string | null>(null);

  // Collect all entity IDs from the review to query threads for
  const allEntityIds = useMemo(
    () => review.sidebarGroups.flatMap((group) => group.items.map((item) => item.id)),
    [review.sidebarGroups]
  );

  const { commentsApi, threads, refetch: refetchThreads } = useCommentThreads(allEntityIds);

  const prevPublishStatusRef = useRef(publishStatus);
  useEffect(() => {
    const wasPublishing = prevPublishStatusRef.current === "publishing";
    const donePublishing = publishStatus === "done" || publishStatus === "error";
    if (wasPublishing && donePublishing) {
      refetchThreads();
    }
    prevPublishStatusRef.current = publishStatus;
  }, [publishStatus, refetchThreads]);

  const commentedItemIds = useMemo(
    () => new Set(threads.filter((t) => t.root.targetId).map((t) => t.root.targetId!)),
    [threads]
  );

  const commentedItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    threads.forEach((t) => {
      if (!t.root.targetId) return;
      counts.set(t.root.targetId, (counts.get(t.root.targetId) ?? 0) + 1);
    });
    return counts;
  }, [threads]);

  const unresolvedItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    threads.forEach((t) => {
      if (!t.root.targetId || t.root.resolved) return;
      counts.set(t.root.targetId, (counts.get(t.root.targetId) ?? 0) + 1);
    });
    return counts;
  }, [threads]);

  // Keyboard navigation: ArrowLeft/ArrowRight to switch between items
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNavigateNext?.();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onNavigatePrev?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onNavigateNext, onNavigatePrev]);

  return (
    <TeamMembersProvider courseId={review.courseId}>
      <View style={styles.page}>
        {!isExpanded && (
          <ReviewTopBar
            review={review}
            reviewedProgress={reviewedProgress}
            lastSavedAt={lastSavedAt}
            isSaving={isSaving}
            commentsCount={threads.length}
            unresolvedCommentsCount={threads.filter((t) => !t.root.resolved).length}
            onExitReview={onExitReview}
            onOpenPublishSheet={onOpenPublishSheet}
            onOpenVersionHistory={onOpenVersionHistory}
            onOpenCommentsSheet={() => setIsCommentsSheetOpen(true)}
          />
        )}
        <View style={styles.workspace}>
          {!isExpanded && (
            <ReviewSidebar
              review={review}
              selectedItemId={selectedItemId}
              filterMode={filterMode}
              collapsedSectionIds={collapsedSectionIds}
              commentedItemIds={commentedItemIds}
              commentedItemCounts={commentedItemCounts}
              unresolvedItemCounts={unresolvedItemCounts}
              getDecision={getDecision}
              onSelectItem={onSelectItem}
              onToggleSection={onToggleSection}
              onFilterModeChange={onFilterModeChange}
              onAddModule={onAddModule}
              onDeleteManualActivity={onDeleteManualActivity}
              deletableManualActivityIds={deletableManualActivityIds}
              onAddActivity={onAddActivity}
              onAcceptGroup={onAcceptGroup}
              onRejectGroup={onRejectGroup}
            />
          )}

          <View style={styles.contentColumn}>
            {isExpanded && selectedReviewItem && (
              <ExpandedReviewContextBar
                itemTypeLabel={getExpandedItemTypeLabel(selectedReviewItem)}
                courseName={review.courseName}
                moduleActivityThread={selectedActivityDetail?.documentReference ?? selectedReviewItem.title}
                activityTitle={selectedReviewItem.title}
                positionIndex={selectedItemPosition + 1}
                totalCount={orderedItems.length}
                whyFindings={selectedActivityDetail?.issueFindingsDetailed}
                whyRubricCode={selectedActivityDetail?.rubricCode}
                whyRubricTitle={selectedActivityDetail?.rubricTitle}
                whyRubricDescription={selectedActivityDetail?.rubricDescription}
                whyLearningObjective={selectedActivityDetail?.learningObjective}
                onCollapse={() => setIsExpanded(false)}
              />
            )}

            <ReviewActionPanel
              selectedReviewItem={selectedReviewItem}
              decision={selectedDecision}
              onAccept={onAccept}
              onReject={onReject}
              onUndo={onUndo}
              onNavigateNext={onNavigateNext}
              onNavigatePrev={onNavigatePrev}
              canAcceptSelectedItem={canAcceptSelectedItem}
              acceptDisabledReason={acceptDisabledReason}
              isExpanded={isExpanded}
              onToggleExpand={handleToggleExpand}
              acceptHiddenReason={acceptHiddenReason}
              commentsApi={commentsApi}
              commentTargetKind={selectedReviewItem?.entityType ?? "assignment"}
              commentTargetId={selectedReviewItem?.id ?? null}
              commentsDisabled={isSelectedItemPublished}
              commentPinOpen={commentPinOpenForItemId === (selectedReviewItem?.id ?? null)}
              onCommentPinOpenChange={(isOpen) => {
                if (isOpen) {
                  setCommentPinOpenForItemId(selectedReviewItem?.id ?? null);
                } else {
                  setCommentPinOpenForItemId(null);
                }
              }}
            />

            <ScrollView
              style={styles.contentScroll}
              contentContainerStyle={[styles.contentScrollContent, isExpanded && styles.contentScrollContentExpanded]}
              scrollEnabled={!isOverviewOverlayVisible}
              tabIndex={0}
              showsVerticalScrollIndicator>
              <View style={[styles.contentInner, isExpanded && styles.contentInnerExpanded]}>
                <ReviewContentPanel
                  review={review}
                  selectedReviewItem={selectedReviewItem}
                  activityDetails={activityDetails}
                  isArchitect={isArchitect}
                  currentUserId={currentUserId}
                  teamManagement={teamManagement}
                  teamUserOptions={teamUserOptions}
                  teamSearchQuery={teamSearchQuery}
                  onTeamSearchQueryChange={onTeamSearchQueryChange}
                  selectedTeamRole={selectedTeamRole}
                  onSelectedTeamRoleChange={onSelectedTeamRoleChange}
                  isSavingTeamManagement={isSavingTeamManagement}
                  onEditingTeamChange={onEditingTeamChange}
                  publishManualSteps={publishManualSteps}
                  isExpanded={isExpanded}
                  isEditDisabled={isSelectedItemPublished}
                  onStartReviewing={onStartReviewing}
                  onOverlayVisibilityChange={setIsOverviewOverlayVisible}
                  onSaveEdit={onSaveEdit}
                  onCreateIle={onCreateIle}
                  onPreviewIle={onPreviewIle}
                  isGeneratingIle={isGeneratingIle}
                  onAccept={onAccept}
                  onReject={onReject}
                  onAcceptWithoutNav={onAccept}
                  onRejectWithoutNav={onReject}
                  onJumpToEntity={onJumpToEntity}
                />
              </View>
            </ScrollView>
          </View>
        </View>

        <PublishSheet
          open={isPublishSheetOpen}
          items={queuedPublishItems}
          publishedItems={lastPublishedItems}
          manualSteps={publishManualSteps}
          documentUpdates={publishDocumentUpdates}
          undecidedCount={undecidedCount}
          lmsLabel={review.sourceLabel}
          canPublishToLms={review.canPublishToLms}
          status={publishStatus}
          onClose={onClosePublishSheet}
          onDone={onDonePublish}
          onPublish={onPublish}
          onExportPdf={onExportPdf}
          onExportImscc={onExportImscc}
          isExportingPdf={isExportingPdf}
          isExportingImscc={isExportingImscc}
        />
        <VersionHistorySheetContainer
          open={isVersionHistoryOpen}
          courseId={review.courseId}
          courseName={review.courseName}
          onClose={onCloseVersionHistory}
        />
        <GlobalCommentsSheet
          open={isCommentsSheetOpen}
          onClose={() => setIsCommentsSheetOpen(false)}
          api={commentsApi}
          items={review.sidebarGroups.flatMap((group) => group.items)}
          onJumpToThread={(_targetKind, targetId) => {
            onSelectItem(targetId);
            setCommentPinOpenForItemId(targetId);
          }}
        />
      </View>
    </TeamMembersProvider>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        } as unknown as ViewStyle)
      : {}),
  },
  workspace: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: colors.grey[50],
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
          overflow: "hidden",
        } as const)
      : {}),
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    backgroundColor: colors.grey[50],
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  contentScrollContent: {
    maxWidth: 1024,
    alignSelf: "center",
    width: "100%",
    padding: 24,
    flexGrow: 1,
  },
  contentScrollContentExpanded: {
    maxWidth: "100%",
    alignSelf: "stretch",
    padding: 0,
  },
  contentInner: {
    width: "100%",
    gap: 16,
  },
  contentInnerExpanded: {
    flex: 1,
    minHeight: 0,
  },
});

export default ReviewPanel;
